import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Hook for managing inline editing state of sidebar items.
 * Handles edit mode toggling, input focus/selection, and save/cancel operations.
 * 
 * @param {string} initialValue - Initial name value (e.g., sprint.name)
 * @param {Function} onSave - Callback when edit is saved with new value
 * @returns {Object} Editing state and handlers
 */
const useItemEditing = (initialValue, onSave) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(initialValue);
  const inputRef = useRef(null);

  // Focus and select input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Sync editValue if initialValue changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditValue(initialValue);
    }
  }, [initialValue, isEditing]);

  const startEditing = useCallback(() => {
    setEditValue(initialValue);
    setIsEditing(true);
  }, [initialValue]);

  const saveEdit = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== initialValue) {
      onSave?.(trimmed);
    }
    setIsEditing(false);
  }, [editValue, initialValue, onSave]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditValue(initialValue);
  }, [initialValue]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  }, [saveEdit, cancelEdit]);

  const handleChange = useCallback((e) => {
    setEditValue(e.target.value);
  }, []);

  return {
    isEditing,
    editValue,
    inputRef,
    startEditing,
    saveEdit,
    cancelEdit,
    handleKeyDown,
    handleChange,
  };
};

export default useItemEditing;
